import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuthProviders } from "@client/app/auth";
import { Card, Spinner } from "@client/components/ui";
import { useT, type CoreKey } from "@client/i18n";

export { safeReturnTo } from "@client/lib/returnTo";
import { returnToFromLocation } from "@client/lib/returnTo";

/**
 * The closed set of `?google=<reason>` values the server redirects here with
 * (spec §10). Anything else is ignored; nothing from the URL is ever shown.
 */
const GOOGLE_REASONS = {
  cancelled: "auth.google.cancelled",
  expired: "auth.google.expired",
  unverified: "auth.google.unverified",
  failed: "auth.google.failed",
  conflict: "auth.google.conflict",
  ratelimited: "auth.google.rateLimited",
} as const satisfies Record<string, CoreKey>;

type GoogleReason = keyof typeof GOOGLE_REASONS;

function isGoogleReason(value: string | null): value is GoogleReason {
  return value !== null && Object.hasOwn(GOOGLE_REASONS, value);
}

/** Google's standard "G" mark, unmodified (branding rules, spec §10). */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const providers = useAuthProviders();
  const from = returnToFromLocation(location);

  // A known `google=` reason is kept in state and the parameter is removed
  // once, so a reload does not repeat the message.
  const [reason, setReason] = useState<GoogleReason | null>(() => {
    const raw = new URLSearchParams(location.search).get("google");
    return isGoogleReason(raw) ? raw : null;
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("google")) return;
    const raw = params.get("google");
    if (isGoogleReason(raw)) setReason(raw);
    params.delete("google");
    const rest = params.toString();
    navigate(
      { pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location, navigate]);

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
      <h1 className="mb-6 text-center font-display text-2xl text-ink-900 dark:text-ink-50">
        {t("auth.signIn")}
      </h1>
      <Card>
        <div className="flex flex-col gap-4">
          {reason && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              {t(GOOGLE_REASONS[reason])}
            </p>
          )}
          {providers.isLoading ? (
            <div className="flex justify-center py-2">
              <Spinner label={t("common.loading")} />
            </div>
          ) : providers.data?.google === false ? (
            <p className="text-sm text-ink-700 dark:text-ink-300">{t("auth.google.disabled")}</p>
          ) : (
            <>
              <a
                href={`/api/auth/google/start?returnTo=${encodeURIComponent(from)}`}
                className="inline-flex min-h-11 items-center justify-center gap-3 rounded-lg border border-[#747775] bg-white px-4 text-sm font-medium text-[#1F1F1F] transition-colors hover:bg-neutral-50"
              >
                <GoogleMark />
                {t("auth.google.signIn")}
              </a>
              <p className="text-sm text-ink-600 dark:text-ink-400">{t("auth.google.firstTime")}</p>
            </>
          )}
          <p className="text-sm text-ink-600 dark:text-ink-400">
            {t("auth.google.sharedComputer")}
          </p>
        </div>
      </Card>
      <p className="mt-6 text-center text-sm text-ink-600 dark:text-ink-400">
        {t("auth.privacyGoogle")}
      </p>
      <Link
        to="/integritet"
        className="mt-4 text-center text-sm text-accent-700 underline dark:text-accent-300"
      >
        {t("privacy.link")}
      </Link>
    </main>
  );
}

export default LoginPage;
